import { Box, Flex, Tag, Text } from '@chakra-ui/react';
import type { MessageDescriptor } from '@lingui/core';
import { msg } from '@lingui/core/macro';
import { useLingui } from '@lingui/react';
import { useSuspenseQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import { BlogGameAction } from '#features/blog/components/BlogGameAction';
import { EditorJsRenderer } from '#features/blog/components/EditorJsRenderer';
import { ReadingProgressBar } from '#features/blog/components/ReadingProgressBar';
import { TableOfContents, type TocItem } from '#features/blog/components/TableOfContents';
import { useTrackBlogView } from '#features/blog/hooks/useTrackBlogView';
import { blogPostBySlugQueryOptions, type BlogPostData } from '#features/blog/model/blog';
import { getReadingTime } from '#features/blog/utils/getReadingTime';
import { useFormatDate } from '#features/date';
import { sharingOptions } from '#features/sharing/constants/sharingOptions';
import { Image, Link, Notice, SectionHeading } from '#features/ui';
import { IconButton } from '#features/ui/components/IconButton';
import { envConfig } from '#constants/envConfig';
import { LegacyBlogContent } from './LegacyBlogContent';
import { prepareLegacyBlogContent } from './legacyContent';

import './wysiwyg-globals.css';

import { useParams } from '@tanstack/react-router';

const shareLabels: Record<string, MessageDescriptor> = {
  facebook: msg({ id: 'blog.share.facebook', message: 'Share on Facebook' }),
  telegram: msg({ id: 'blog.share.telegram', message: 'Share on Telegram' }),
  reddit: msg({ id: 'blog.share.reddit', message: 'Share on Reddit' }),
  x: msg({ id: 'blog.share.x', message: 'Share on X' }),
};

const parseEditorJsContent = (content: string) => {
  try {
    const parsed = JSON.parse(content);
    if (parsed && Array.isArray(parsed.blocks)) {
      return parsed;
    }
  } catch {
    // Not JSON — legacy HTML content
  }
  return null;
};

const stripHtml = (html: string): string => html.replaceAll(/<[^>]*>/g, '');

const slugify = (text: string): string =>
  text
    .toLowerCase()
    .replaceAll(/[^a-zа-яё0-9\s-]/gi, '')
    .replaceAll(/\s+/g, '-')
    .replaceAll(/-+/g, '-')
    .slice(0, 60);

const extractTocItems = (content: string): TocItem[] => {
  const editorJs = parseEditorJsContent(content);
  if (!editorJs) return [];

  return editorJs.blocks
    .filter(
      (block: { type: string; data: Record<string, unknown> }) =>
        block.type === 'header' &&
        ((block.data.level as number) === 2 || (block.data.level as number) === 3)
    )
    .map((block: { type: string; data: Record<string, unknown> }, index: number) => {
      const text = stripHtml((block.data.text as string) ?? '');
      return {
        id: `heading-${index}-${slugify(text)}`,
        text,
        level: block.data.level as number,
      };
    });
};

/** The public article: fetched by the address, rendered by BlogPost below. */
export const BlogPostBySlug = () => {
  const { slug } = useParams({ from: '/{-$locale}/_layout/blog/$slug' });
  const { data: post } = useSuspenseQuery(blogPostBySlugQueryOptions(slug));

  return <BlogPost post={post} slug={slug} />;
};

type BlogPostProps = {
  post: BlogPostData;
  slug: string;
  /**
   * The admin looking at an unpublished article through /blog/preview. Same page, same
   * components; only the reader-side effects go: no view is counted, and there is nothing to
   * share yet. A banner says so.
   */
  preview?: boolean;
};

export const BlogPost = ({ post, slug, preview = false }: BlogPostProps) => {
  const { _, i18n } = useLingui();
  const formatDate = useFormatDate();

  const { date, title, content, ogTitle, previewImage, tags } = post;

  // Counts a view once the article is actually rendered for a reader in the browser.
  useTrackBlogView(preview ? null : post.id);

  const postLink = `${envConfig.HOST}/blog/${slug}?utm_source=share&utm_medium=link`;
  const linkTitle = `Teamplay.gg - ${ogTitle} `;

  const readingTime = useMemo(
    () => post.readingTime ?? getReadingTime(content),
    [content, post.readingTime]
  );
  const editorJs = useMemo(() => parseEditorJsContent(content), [content]);
  const legacy = useMemo(
    () => (editorJs ? null : prepareLegacyBlogContent(content)),
    [content, editorJs]
  );
  const tocItems = useMemo(() => legacy?.tocItems ?? extractTocItems(content), [content, legacy]);
  const gameId =
    typeof editorJs?._gameId === 'string' &&
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(editorJs._gameId)
      ? editorJs._gameId
      : null;
  const gameName = tags?.split(',')[0]?.trim();
  const postExampleImage =
    typeof editorJs?._postExampleImage === 'string' ? editorJs._postExampleImage : undefined;
  const leadBlock = editorJs?.blocks[0]?.type === 'paragraph' ? editorJs.blocks[0] : null;
  const articleContent = leadBlock ? { ...editorJs, blocks: editorJs.blocks.slice(1) } : editorJs;
  const sourcesIndex =
    gameId && articleContent
      ? articleContent.blocks.findIndex((block: { id?: string }) => block.id === 'official-sources')
      : -1;
  const mainBlocks =
    sourcesIndex >= 0 ? articleContent.blocks.slice(0, sourcesIndex) : articleContent?.blocks;
  const sourceBlocks = sourcesIndex >= 0 ? articleContent.blocks.slice(sourcesIndex) : null;
  const headingOffset =
    mainBlocks?.filter(
      (block: { type: string; data: { level?: number } }) =>
        block.type === 'header' && (block.data.level === 2 || block.data.level === 3)
    ).length ?? 0;

  return (
    <>
      <ReadingProgressBar />
      <Box maxW="1100px" w="100%" px={{ base: 4, md: 6 }}>
        {preview && (
          <Notice
            tone="warning"
            mb={6}
            title={_(msg({ id: 'blog.preview.title', message: 'Draft preview' }))}
          >
            {_(
              msg({
                id: 'blog.preview.body',
                message:
                  'Not published: this address is not indexed and views are not counted. The link works for 20 minutes; open it again from the admin panel.',
              })
            )}
          </Notice>
        )}
        {/* Breadcrumb */}
        <Flex gap={2} alignItems="center" mb={6} textStyle="sm" color="fg.muted">
          <Link to="/{-$locale}/blog" _hover={{ color: 'whiteAlpha.800' }}>
            {_(msg({ id: 'blog.breadcrumb.blog', message: 'Blog' }))}
          </Link>
          <Text>/</Text>
          {tags && (
            <>
              <Link
                to="/{-$locale}/blog"
                search={{ tag: tags.split(',')[0] }}
                color="fg.muted"
                _hover={{ color: 'whiteAlpha.800' }}
              >
                {tags.split(',')[0]}
              </Link>
              <Text>/</Text>
            </>
          )}
          <Text color="fg.secondary" lineClamp={1}>
            {title}
          </Text>
        </Flex>

        {/* Title */}
        <Text as="h1" textStyle="article.h1" mb={4}>
          {title}
        </Text>

        {/* Meta row: tags + date + reading time */}
        <Flex gap={{ base: 3, md: 4 }} alignItems="center" flexWrap="wrap" mb={8}>
          {tags && (
            <Flex gap={2} flexWrap="wrap">
              {tags
                .split(',')
                .map((tag) => tag.trim())
                .filter(Boolean)
                .map((tag) => (
                  <Tag.Root key={tag} size="md">
                    <Tag.Label textStyle="label">{tag}</Tag.Label>
                  </Tag.Root>
                ))}
            </Flex>
          )}
          {date && (
            <Text color="fg.muted" textStyle="sm">
              {formatDate(date, 'dd MMMM yyyy')}
            </Text>
          )}
          <Text color="fg.muted" textStyle="sm">
            ·
          </Text>
          <Text color="fg.muted" textStyle="sm">
            {readingTime} {_(msg({ id: 'blog.readingTime', message: 'min read' }))}
          </Text>
        </Flex>

        {leadBlock && (
          <Box maxW="720px" mb={6}>
            <EditorJsRenderer content={{ ...editorJs, blocks: [leadBlock] }} />
          </Box>
        )}

        {/* Hero image */}
        <Box w="100%" aspectRatio="16 / 9" borderRadius="xl" overflow="hidden" mb={10}>
          <Image
            src={previewImage}
            alt={title}
            width={1100}
            style={{
              objectFit: 'cover',
              objectPosition: 'top center',
              width: '100%',
              height: '100%',
            }}
          />
        </Box>

        <TableOfContents items={tocItems} variant="inline" />

        {/* Content + Table of Contents */}
        <Flex gap={10} alignItems="flex-start">
          {/* Main content */}
          <Box flex={1} minW={0} maxW="720px">
            {(() => {
              if (articleContent && mainBlocks) {
                return (
                  <EditorJsRenderer
                    content={{ ...articleContent, blocks: mainBlocks }}
                    tocItems={tocItems}
                  />
                );
              }
              return <LegacyBlogContent html={legacy?.html ?? content} />;
            })()}

            {gameId && (
              <BlogGameAction
                gameId={gameId}
                gameName={gameName}
                slug={slug}
                exampleImage={postExampleImage}
              />
            )}

            {sourceBlocks && articleContent && (
              <EditorJsRenderer
                content={{ ...articleContent, blocks: sourceBlocks }}
                tocItems={tocItems}
                headingOffset={headingOffset}
              />
            )}

            {/* Share section */}
            {!preview && (
              <Box
                data-testid="blog-share"
                mt={16}
                mb={10}
                pt={8}
                borderTopWidth="1px"
                borderColor="border"
              >
                <Box mb={4}>
                  <SectionHeading>
                    {_(msg({ id: 'blog.share', message: 'Share this article' }))}
                  </SectionHeading>
                </Box>
                <Flex gap={0}>
                  {sharingOptions.map(({ icon, name, url }) => (
                    <IconButton
                      key={name}
                      aria-label={i18n._(shareLabels[name])}
                      variant="ghost"
                      size="lg"
                      css={{ '& svg': { boxSize: 7 } }}
                      asChild
                    >
                      <a
                        aria-label={i18n._(shareLabels[name])}
                        href={url(postLink, linkTitle)}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {icon}
                      </a>
                    </IconButton>
                  ))}
                </Flex>
              </Box>
            )}

            {/* Back to blog */}
            <Box mb={10}>
              <Link
                to="/{-$locale}/blog"
                textStyle="title"
                color="purple.400"
                _hover={{ color: 'purple.300' }}
              >
                ← {_(msg({ id: 'blog.backToBlog', message: 'Back to all articles' }))}
              </Link>
            </Box>
          </Box>

          {/* Table of Contents sidebar */}
          <TableOfContents items={tocItems} />
        </Flex>
      </Box>
    </>
  );
};

BlogPost.getLayout = null;
